import * as THREE from "three";
import type { LineMaterialParameters } from "three/examples/jsm/lines/LineMaterial.js";
import type { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import { HIGHLIGHTABLE_LAYER, RAYCASTABLE_LAYER, SELECTABLE_LAYER } from "../../../layers";
import { offsetPolygon, splitAtSelfIntersection } from "../../geometry";
import { createThickLineSegments, INVALID_COLOR, setThickLinePoints, VertexHandle } from "../_shared";
import { Polyline } from "../Polyline/Polyline";

const DEFAULT_FILL_COLOR = 0x6b7280;

const FILL_MATERIAL_DEFINITION: THREE.MeshBasicMaterialParameters = {
	color: DEFAULT_FILL_COLOR,
	transparent: true,
	opacity: 0.25,
	side: THREE.DoubleSide,
	depthTest: true,
	depthWrite: false,
};

const SELECTED_FILL_OPACITY_BOOST = 0.25;

const INVALID_MARKER_COLOR = "#e24b4a";

const DISCARD_FILL_MATERIAL_DEFINITION: THREE.MeshBasicMaterialParameters = {
	color: INVALID_COLOR,
	transparent: true,
	opacity: 0.18,
	side: THREE.DoubleSide,
	depthTest: false,
	depthWrite: false,
};

const INVALID_EDGE_WIDTH_PX = 4;

const INVALID_EDGE_MATERIAL_DEFINITION: LineMaterialParameters = {
	color: INVALID_COLOR,
	linewidth: INVALID_EDGE_WIDTH_PX,
	transparent: true,
	depthTest: false,
	depthWrite: false,
};

/**
 * Polygon is a closed Polyline with a fill
 */
class Polygon extends Polyline {
	public readonly fill: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;

	private readonly discardFill: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;
	private readonly invalidEdges: LineSegments2;
	private readonly intersectionMarkers: Array<VertexHandle> = [];

	private readonly fillMaterialDefinition: THREE.MeshBasicMaterialParameters;
	private fillColor: THREE.ColorRepresentation;

	private readonly baseFillOpacity: number;

	constructor({
		label,
		labelColor,
		lineColor,
		vertexHandlesColor,
		vertexHandlesFillColor,
		vertexHandlesHighlightColor,
		highlightedEdgeColor,
		selectedEdgeColor,
		fillColor,
		fillOpacity,
	}: {
		label?: string;
		labelColor?: THREE.ColorRepresentation;
		lineColor?: THREE.ColorRepresentation;
		vertexHandlesColor?: THREE.ColorRepresentation;
		vertexHandlesFillColor?: THREE.ColorRepresentation;
		vertexHandlesHighlightColor?: THREE.ColorRepresentation;
		highlightedEdgeColor?: THREE.ColorRepresentation;
		selectedEdgeColor?: THREE.ColorRepresentation;
		fillColor?: THREE.ColorRepresentation;
		fillOpacity?: number;
	} = {}) {
		super({
			label,
			labelColor,
			lineColor,
			vertexHandlesColor,
			vertexHandlesFillColor,
			vertexHandlesHighlightColor,
			highlightedEdgeColor,
			selectedEdgeColor,
			isLoop: true,
		});

		this.baseFillOpacity = fillOpacity ?? FILL_MATERIAL_DEFINITION.opacity ?? 0.25;
		this.fillColor = fillColor ?? DEFAULT_FILL_COLOR;

		this.fillMaterialDefinition = {
			...FILL_MATERIAL_DEFINITION,
			color: this.fillColor,
			opacity: this.baseFillOpacity,
		};

		this.fill = this.createFill();
		this.sceneObject.add(this.fill);

		this.discardFill = this.createDiscardFill();
		this.sceneObject.add(this.discardFill);

		this.invalidEdges = this.createInvalidEdges();
		this.sceneObject.add(this.invalidEdges);
	}

	public override dispose(): void {
		this.fill.geometry.dispose();
		this.fill.material.dispose();

		this.discardFill.geometry.dispose();
		this.discardFill.material.dispose();

		this.invalidEdges.geometry.dispose();
		this.invalidEdges.material.dispose();

		for (const marker of this.intersectionMarkers) {
			marker.dispose();
		}

		this.intersectionMarkers.length = 0;

		super.dispose();
	}

	public override select(): void {
		super.select();
		this.fill.material.opacity = Math.min(1, this.baseFillOpacity + SELECTED_FILL_OPACITY_BOOST);
	}

	public override deselect(): void {
		super.deselect();
		this.fill.material.opacity = this.baseFillOpacity;
	}

	public setFillColor(color: THREE.ColorRepresentation): void {
		this.fillColor = color;
		this.applyFillColor();
	}

	public override setInvalid(isInvalid: boolean): void {
		super.setInvalid(isInvalid);
		this.applyFillColor();
	}

	private applyFillColor(): void {
		this.fill.material.color.set(this.isMarkedInvalid() ? INVALID_COLOR : this.fillColor);
	}

	/**
	 * Offsets every edge of the ring. Positive shrinks the shape and negative grows it, whichever
	 * way the vertices were drawn. Returns false when the inset collapses the shape.
	 */
	public override offset({ distance }: { distance: number }): boolean {
		const points = offsetPolygon({ points: this.getPoints(), distance });

		if (points === null) {
			return false;
		}

		this.setPoints({ points });
		return true;
	}

	protected override afterShapeViewSynchronized(shapeVertices: ReadonlyArray<THREE.Vector3>): void {
		this.syncFill({ shapeVertices });
		this.syncSelfIntersectionPreview();
	}

	private syncFill({ shapeVertices }: { shapeVertices: ReadonlyArray<THREE.Vector3> }): void {
		this.fill.geometry.dispose();

		if (shapeVertices.length < 3) {
			this.fill.geometry = new THREE.BufferGeometry();
			this.fill.visible = false;
			return;
		}

		this.fill.geometry = createRingGeometry({ ring: shapeVertices });
		this.fill.visible = true;
	}

	private syncSelfIntersectionPreview(): void {
		const intersections = this.getSelfIntersections();

		this.syncDiscardFill({ intersections });
		this.syncInvalidEdges({ intersections });
		this.syncIntersectionMarkers({ intersections });
	}

	private syncDiscardFill({ intersections }: { intersections: ReturnType<Polyline["getSelfIntersections"]> }): void {
		this.discardFill.geometry.dispose();

		if (intersections.length === 0) {
			this.discardFill.geometry = new THREE.BufferGeometry();
			this.discardFill.visible = false;
			return;
		}

		const { discard } = splitAtSelfIntersection({ points: this.getPoints(), intersection: intersections[0] });

		if (discard.length < 3) {
			this.discardFill.geometry = new THREE.BufferGeometry();
			this.discardFill.visible = false;
			return;
		}

		this.discardFill.geometry = createRingGeometry({ ring: discard });
		this.discardFill.visible = true;
	}

	private syncInvalidEdges({ intersections }: { intersections: ReturnType<Polyline["getSelfIntersections"]> }): void {
		if (intersections.length === 0) {
			setThickLinePoints({ line: this.invalidEdges, points: [] });
			return;
		}

		const points = this.getPoints();
		const invalidEdgeIndices = new Set<number>();

		for (const intersection of intersections) {
			invalidEdgeIndices.add(intersection.firstEdgeIndex);
			invalidEdgeIndices.add(intersection.secondEdgeIndex);
		}

		const endpoints: Array<THREE.Vector3> = [];

		for (const edgeIndex of invalidEdgeIndices) {
			endpoints.push(points[edgeIndex], points[(edgeIndex + 1) % points.length]);
		}

		setThickLinePoints({ line: this.invalidEdges, points: endpoints });
	}

	private syncIntersectionMarkers({ intersections }: { intersections: ReturnType<Polyline["getSelfIntersections"]> }): void {
		while (this.intersectionMarkers.length < intersections.length) {
			const marker = new VertexHandle({ color: INVALID_MARKER_COLOR, fillColor: INVALID_MARKER_COLOR });
			this.intersectionMarkers.push(marker);
			this.sceneObject.add(marker.sceneObject);
		}

		while (this.intersectionMarkers.length > intersections.length) {
			this.intersectionMarkers.pop()?.dispose();
		}

		for (let index = 0; index < this.intersectionMarkers.length; index++) {
			this.intersectionMarkers[index].update({ position: intersections[index].point, isVisible: true });
		}
	}

	private createFill(): THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial> {
		const mesh = new THREE.Mesh(new THREE.BufferGeometry(), new THREE.MeshBasicMaterial(this.fillMaterialDefinition));
		mesh.layers.enable(RAYCASTABLE_LAYER);
		mesh.layers.enable(HIGHLIGHTABLE_LAYER);
		mesh.layers.enable(SELECTABLE_LAYER);
		mesh.frustumCulled = false;
		mesh.visible = false;
		return mesh;
	}

	// The invalid-shape overlays are pure feedback: no picking layers, drawn on top of the fill.
	private createDiscardFill(): THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial> {
		const mesh = new THREE.Mesh(new THREE.BufferGeometry(), new THREE.MeshBasicMaterial(DISCARD_FILL_MATERIAL_DEFINITION));
		mesh.frustumCulled = false;
		mesh.visible = false;
		return mesh;
	}

	private createInvalidEdges(): LineSegments2 {
		const lineSegments = createThickLineSegments({ materialDefinition: INVALID_EDGE_MATERIAL_DEFINITION });
		lineSegments.visible = false;
		return lineSegments;
	}
}

// The sketch plane is XZ, so rings are flattened to (x, z) and the resulting geometry laid back down.
function createRingGeometry({ ring }: { ring: ReadonlyArray<THREE.Vector3> }): THREE.ShapeGeometry {
	const shape = new THREE.Shape();
	shape.moveTo(ring[0].x, ring[0].z);

	for (let index = 1; index < ring.length; index++) {
		shape.lineTo(ring[index].x, ring[index].z);
	}

	const geometry = new THREE.ShapeGeometry(shape);
	geometry.rotateX(Math.PI / 2);
	return geometry;
}

export { Polygon };
